import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { Users, X, ChevronRight } from 'lucide-react';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useToast } from '@/components/ui/Toast';
import { UPDATE_INVITED_USER_ROLE } from '@/graphql/mutations';
import { InvitedUserRole } from '@/gql/graphql';

const MAX_AVATARS = 5;

interface UserInfo {
    id?: string | null;
    username?: string | null;
    pictureUrl?: string | null;
}

interface InvitedUser {
    user?: UserInfo | null;
    role?: InvitedUserRole | null;
}

interface ParticipantsCardProps {
    participants: InvitedUser[];
    creator?: UserInfo | null;
    canManage: boolean;
    raceId: string;
}

export function ParticipantsCard({ participants, creator, canManage, raceId }: ParticipantsCardProps) {
    const { t } = useTranslation('races');
    const [showModal, setShowModal] = useState(false);

    // Build full list: creator first, then invited users (deduplicated)
    const validUsers = participants.map(p => p.user).filter((u): u is UserInfo => u != null);
    const allUsers = creator
        ? [creator, ...validUsers.filter(u => u.id !== creator.id)]
        : validUsers;

    if (allUsers.length === 0) return null;

    const displayed = allUsers.slice(0, MAX_AVATARS);
    const extraCount = allUsers.length - displayed.length;

    return (
        <>
            <button
                type="button"
                onClick={() => setShowModal(true)}
                className="w-full text-left rounded-2xl bg-violet-50/50 p-4 border border-violet-100 hover:border-violet-200 transition-colors"
            >
                <div className="flex items-center gap-2 mb-3">
                    <Users size={16} className="text-violet-600" />
                    <span className="text-sm font-bold text-violet-800">
                        {t('details.participants.title')}
                    </span>
                    <ChevronRight size={14} className="text-violet-400 ml-auto" />
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                        {displayed.map(u => (
                            <UserAvatar
                                key={u.id}
                                src={u.pictureUrl}
                                alt={u.username ?? ''}
                                size="xs"
                                className="border-2 border-violet-100"
                            />
                        ))}
                        {extraCount > 0 && (
                            <div className="w-8 h-8 rounded-full bg-violet-100 border-2 border-violet-50 flex items-center justify-center flex-shrink-0">
                                <span className="text-[10px] font-bold text-violet-700">+{extraCount}</span>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-violet-800">
                            {t('details.participants.count', { count: allUsers.length })}
                        </p>
                        <p className="text-xs text-violet-600 truncate">
                            {displayed.map(u => u.username).join(', ')}
                            {extraCount > 0 && '...'}
                        </p>
                    </div>
                </div>
            </button>

            {showModal && (
                <ParticipantsModal
                    participants={participants}
                    creator={creator}
                    canManage={canManage}
                    raceId={raceId}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
}

function ParticipantsModal({ participants, creator, canManage, raceId, onClose }: {
    participants: InvitedUser[];
    creator?: UserInfo | null;
    canManage: boolean;
    raceId: string;
    onClose: () => void;
}) {
    const { t } = useTranslation('races');
    const { toast } = useToast();
    const [updateRole] = useMutation(UPDATE_INVITED_USER_ROLE);

    const creatorId = creator?.id;

    // Full list: creator first, then invited users (deduplicated)
    const allEntries: { user: UserInfo; role?: InvitedUserRole | null; isCreator: boolean }[] = [];
    if (creator) {
        allEntries.push({ user: creator, role: null, isCreator: true });
    }
    for (const p of participants) {
        if (p.user && p.user.id !== creatorId) {
            allEntries.push({ user: p.user, role: p.role, isCreator: false });
        }
    }

    const handleRoleChange = async (userId: string, role: InvitedUserRole) => {
        try {
            await updateRole({ variables: { input: { raceId, userId, role } } });
        } catch {
            toast(t('details.participants.roleError'), 'error');
        }
    };

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={onClose}
            />

            <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-sm relative z-10 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200 border border-slate-100 max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center gap-3 p-5 pb-4 border-b border-slate-100 flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                        <Users size={16} className="text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900">
                            {t('details.participants.title')}
                        </p>
                        <p className="text-xs text-slate-400">
                            {t('details.participants.count', { count: allEntries.length })}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* List */}
                <div className="overflow-y-auto p-3 space-y-1">
                    {allEntries.map(entry => (
                        <div key={entry.user.id} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50">
                            <UserAvatar
                                src={entry.user.pictureUrl}
                                alt={entry.user.username ?? ''}
                                size="xs"
                            />
                            <span className="text-sm font-medium text-slate-800 flex-1 truncate">
                                {entry.user.username}
                            </span>
                            {entry.isCreator ? (
                                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">
                                    {t('details.participants.creator')}
                                </span>
                            ) : canManage && entry.role ? (
                                <select
                                    value={entry.role}
                                    onChange={(e) => handleRoleChange(entry.user.id!, e.target.value as InvitedUserRole)}
                                    className="text-xs font-medium px-2 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                                >
                                    <option value={InvitedUserRole.Viewer}>{t('details.participants.role.viewer')}</option>
                                    <option value={InvitedUserRole.Editor}>{t('details.participants.role.editor')}</option>
                                </select>
                            ) : entry.role ? (
                                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                                    entry.role === InvitedUserRole.Editor
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-slate-100 text-slate-500'
                                }`}>
                                    {t(`details.participants.role.${entry.role.toLowerCase()}`)}
                                </span>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body,
    );
}
